import { OptionsType } from '@openx/types/options';
import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig';
import { useAppInventoryFetch } from './utils/content/useAppInventoryFetch';
import { useAppNameBulkValidationV2 } from './utils/content/useAppNameBulkValidationV2';
import { useFetchInventoriesBySearchText } from './utils/content/useFetchInventoriesBySearchText';
import { useFetchInventoryInstances } from './utils/content/useFetchInventoryInstances';
import { useFetchInventoryMarket } from './utils/content/useFetchInventoryMarket';
import { useInventoriesFetch } from './utils/content/useInventoriesFetch';
import { usePmpSizesFetch } from './utils/content/usePmpSizesFetch';
import { useResolveAppGroupIds } from './utils/content/useResolveAppGroupIds';
import { useCountriesOptionsFetch } from './utils/geodata/useCountriesOptionsFetch';
import { useFetchByGeoData } from './utils/geodata/useFetchByGeoData';
import { useFetchBySearchText } from './utils/geodata/useFetchBySearchText';
import { usePostalCodes } from './utils/geodata/usePostalCodes';
import { useAdPlacementOptionsFetch } from './utils/inventoryFormat/useAdPlacementOptionsFetch';
import { useDistributionChannelOptionsFetch } from './utils/inventoryFormat/useDistributionChannelOptionsFetch';
import { useOldAdPlacementOptionsFetch } from './utils/inventoryFormat/useOldAdPlacementOptionsFetch';
import { useOldDistributionChannelOptionsFetch } from './utils/inventoryFormat/useOldDistributionChannelOptionsFetch';
import { useAccountOptionsFetch } from './utils/useAccountOptionsFetch';
import { useAccountUid } from './utils/useAccountUid';
import { useAudienceCreateAndActivate } from './utils/useAudienceCreateAndActivate';
import { useAudienceOptionsDetailsFetch } from './utils/useAudienceOptionsDetailsFetch';
import { useAudienceOptionsDetailsLazyFetch } from './utils/useAudienceOptionsDetailsLazyFetch';
import { useAudienceOptionsFetch } from './utils/useAudienceOptionsFetch';
import { useCategoriesOptionsFetch } from './utils/useCategoriesOptionsFetch';
import { useContentObjectOptionsConfig } from './utils/useContentObjectOptionsConfig';
import { useContextualDataPartnerOptions } from './utils/useContextualDataPartnerOptions';
import { useCurrentInstanceState } from './utils/useCurrentInstanceState';
import { useDeviceTypesOptionsFetch } from './utils/useDeviceTypesOptionsFetch';
import { useFetchSegments } from './utils/useFetchSegments';
import { useFetchSegmentsV2 } from './utils/useFetchSegmentsV2';
import { useGetAudienceMultiproviderInfo } from './utils/useGetAudienceMultiproviderInfo';
import { useGetContextualPricing } from './utils/useGetContextualPricing';
import { useInventoryListByIdFetch } from './utils/useInventoryListByIdFetch';
import { useInventoryListsOptionsFetch } from './utils/useInventoryListsOptionsFetch';
import { useIsRetargetingAllowed } from './utils/useIsRetargetingAllowed';
import { useKeywordsOptionsFetch } from './utils/useKeywordsOptionsFetch';
import { useMetacategoriesFetch } from './utils/useMetacategoriesFetch';
import { useRelatedAccountsFetch } from './utils/useRelatedAccountsFetch';
import { useRetargetingSegmentDetailsFetch } from './utils/useRetargetingSegmentDetailsFetch';
import { useRetargetingSegmentsOptionsFetch } from './utils/useRetargetingSegmentsOptionsFetch';
import { useSelectRateOptionsFetch } from './utils/useSelectRateOptionsFetch';
import { useTargetingBlockingRulesSchemaFetch } from './utils/useTargetingBlockingRulesSchemaFetch';
import { useTechnologyOptionsFetch } from './utils/useTechonologyOptionsFetch';
import { useVideoOptionsFetch } from './utils/useVideoOptionsFetch';

export const targetingConfig: TargetingProviderConfig = {
  isOa: true,
  useAppInventoryFetch: useAppInventoryFetch,
  useAppNameBulkValidationV2: useAppNameBulkValidationV2,
  useResolveAppGroupIds: useResolveAppGroupIds,
  useAccountOptionsFetch,
  useAccountUid,
  useAdPlacementOptions: () => {
    const { mapById, loading, refetch } = useAdPlacementOptionsFetch();

    return { adPlacement: mapById, isAdPlacementLoading: loading, refetch };
  },
  useOldAdPlacementOptions: () => {
    const { mapById, loading, refetch } = useOldAdPlacementOptionsFetch();

    return { adPlacement: mapById, isAdPlacementLoading: loading, refetch };
  },
  useAudienceOptionsDetailsFetch,
  useAudienceOptionsDetailsLazyFetch,
  useAudienceCreateAndActivate,
  useAudienceOptionsFetch,
  useCategoriesFetch: useCategoriesOptionsFetch,
  useContentObjectOptionsConfig,
  useCountriesOptionsFetch,
  useCurrentInstanceState,
  useDeviceTypesOptionsFetch: () => {
    const deviceTypesOptions = useDeviceTypesOptionsFetch();

    return {
      [OptionsType.DESKTOP_DEVICES]: {
        data: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.DESKTOP_DEVICES].refetch || (() => Promise.resolve()),
      },
      [OptionsType.MOBILE_DEVICES]: {
        data: deviceTypesOptions[OptionsType.MOBILE_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.MOBILE_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.MOBILE_DEVICES].refetch || (() => Promise.resolve()),
      },
      [OptionsType.TV_DEVICES]: {
        data: deviceTypesOptions[OptionsType.TV_DEVICES].mapById,
        isLoading: deviceTypesOptions[OptionsType.TV_DEVICES].loading,
        refetch: deviceTypesOptions[OptionsType.TV_DEVICES].refetch || (() => Promise.resolve()),
      },
    };
  },
  useDistributionChannelOptions: () => {
    const { mapById, loading, refetch } = useDistributionChannelOptionsFetch();

    return {
      distributionChannel: mapById,
      isDistributionChannelLoading: loading,
      refetch,
    };
  },
  useOldDistributionChannelOptions: () => {
    const { mapById, loading, refetch } = useOldDistributionChannelOptionsFetch();

    return {
      distributionChannel: mapById,
      isDistributionChannelLoading: loading,
      refetch,
    };
  },
  useFetchByGeoData,
  useFetchBySearchText,
  useFetchInventoriesBySearchText,
  useFetchInventoryInstances,
  useFetchInventoryMarket,
  useFetchSegments,
  useFetchSegmentsV2,
  useContextualDataPartnerOptions,
  useInventoriesFetch,
  useKeywordsOptionsFetch,
  useMetacategoriesFetch,
  usePmpSizesFetch,
  usePostalCodes,
  useRelatedAccountsFetch,
  useRetargetingSegmentsOptionsFetch,
  useRetargetingSegmentDetailsFetch,
  useIsRetargetingAllowed,
  useSelectRateOptionsFetch,
  useTechnologyOptionsFetch,
  useVideoOptionsFetch,
  useInventoryListsOptionsFetch,
  useInventoryListByIdFetch,
  useGetAudienceMultiproviderInfo,
  useGetContextualPricing,
  useTargetingBlockingRulesSchemaFetch,
};
